import { BehaviorSubject, combineLatest, filter, from, switchMap } from "rxjs";
import { getDb, type RecordDoc } from "@/lib/db";
import { resolveScreenBand } from "@/lib/format";
import { loggedDateOf } from "@/lib/loggedDate";
import { getCurrentProfile, profile$ } from "@/lib/profile";
import { awaitInitialReplication } from "@/lib/replication";
import {
  boundsFromWeekRecord,
  currentWeekBounds,
  formatDateOnly,
  formatWeekRange,
  parseDateOnly,
  transitionWeekAfter,
  type WeekBounds,
} from "@/lib/week";
import { familyService } from "@/services/family.service";
import type {
  JobLogData,
  JobLogRecord,
  JobRecord,
  WeekData,
  WeekRecord,
} from "@/types/domain";

interface WeekLogState {
  // Active week the child is operating on. This is the current ISO week, OR
  // the previous ISO week when it is still `open` (locking the child out of
  // the current week until they confirm).
  week: WeekRecord | null;
  logs: JobLogRecord[];
  siblingTotals: Record<string, number>;
  // True when `week` refers to the previous ISO week (i.e. the child is
  // stuck on last week because it isn't confirmed yet).
  isPreviousWeek: boolean;
  // Set when the previous ISO week is in `child_confirmed` — the child has
  // done their part but it isn't closed yet. Used for the yellow "venter på
  // foreldre" pill.
  pendingPreviousWeek: WeekRecord | null;
  // All weeks + logs for the current child. Used by archive surfaces
  // (ChildHistory) and the post-close summary callout on ChildHome.
  allWeeks: WeekRecord[];
  allLogs: JobLogRecord[];
  loading: boolean;
}

const state$ = new BehaviorSubject<WeekLogState>({
  week: null,
  logs: [],
  siblingTotals: {},
  isPreviousWeek: false,
  pendingPreviousWeek: null,
  allWeeks: [],
  allLogs: [],
  loading: true,
});

const childAuth$ = profile$.pipe(
  filter((s) => !s.loading && s.profile?.data.role === "child"),
);

const records$ = childAuth$.pipe(
  switchMap((s) =>
    from(getDb()).pipe(
      switchMap((db) =>
        combineLatest([
          db.week_allowance.find({
            selector: {
              family_id: s.profile!.data.family_id,
              type: "week",
            },
          }).$,
          db.week_allowance.find({
            selector: {
              family_id: s.profile!.data.family_id,
              type: "job_log",
            },
          }).$,
        ]),
      ),
    ),
  ),
);

// Create-on-demand: only fires when the child actually logs a job. Avoids
// racing with replication during external delete/reseed (e.g. E2E resets),
// which previously produced phantom week records.
let inFlightCreate: Promise<WeekRecord> | null = null;

function buildWeekData(bounds: WeekBounds): WeekData {
  return {
    iso_week: bounds.iso_week,
    year: bounds.year,
    status: "open",
    week_range_label: formatWeekRange(bounds.start, bounds.end),
    start_date: formatDateOnly(bounds.start),
    end_date: formatDateOnly(bounds.end),
    start_day_offset: bounds.start_day_offset,
    child_confirmed_at: null,
    closed_at: null,
    approved_by: null,
    bank_balance_after: null,
    stock_value_after: null,
    total_invested: null,
    streak: 0,
    screen_time_hours: null,
    screen_time_amount: null,
  };
}

/**
 * Bounds for the next week to create for `userId`. Normally
 * `weekContaining(today)` under the family's current offset; if that would
 * overlap a previous week (offset changed mid-flight), produces a transition
 * week starting the day after the previous week ends.
 */
function nextWeekBoundsForUser(
  weeks: WeekRecord[],
  userId: string,
  today: Date,
): WeekBounds {
  const offset = familyService.weekStartDay();
  const natural = currentWeekBounds(offset, today);
  const myWeeks = weeks.filter((w) => w.user_id === userId);
  if (myWeeks.length === 0) return natural;
  const latestEnd = myWeeks
    .map((w) => parseDateOnly(w.data.end_date ?? "1970-01-01"))
    .sort((a, b) => b.getTime() - a.getTime())[0];
  if (latestEnd >= natural.start) {
    return transitionWeekAfter(latestEnd, offset);
  }
  return natural;
}

async function getOrCreateCurrentWeek(
  userId: string,
  familyId: string,
): Promise<WeekRecord> {
  if (inFlightCreate) return inFlightCreate;

  const create = (async () => {
    await awaitInitialReplication();
    const db = await getDb();
    const existingDocs = await db.week_allowance
      .find({
        selector: { family_id: familyId, user_id: userId, type: "week" },
      })
      .exec();
    const myWeeks = existingDocs.map(
      (d) => d.toJSON() as RecordDoc as unknown as WeekRecord,
    );
    const today = new Date();
    const todayStr = formatDateOnly(today);
    const existing = myWeeks.find((w) => {
      const start = w.data.start_date ?? null;
      const end = w.data.end_date ?? null;
      if (start && end) return start <= todayStr && todayStr <= end;
      const bounds = boundsFromWeekRecord(w.data);
      return bounds.start <= today && today <= bounds.end;
    });
    if (existing) return existing;

    const bounds = nextWeekBoundsForUser(myWeeks, userId, today);
    const weekData = buildWeekData(bounds);
    const now = new Date().toISOString();
    const doc: RecordDoc = {
      id: crypto.randomUUID(),
      type: "week",
      family_id: familyId,
      user_id: userId,
      created_at: now,
      updated_at: now,
      data: weekData as unknown as Record<string, unknown>,
    };
    await db.week_allowance.insert(doc);
    return doc as unknown as WeekRecord;
  })();

  inFlightCreate = create;
  try {
    return await create;
  } finally {
    inFlightCreate = null;
  }
}

function weekContainsDate(week: WeekRecord, date: Date): boolean {
  const dateStr = formatDateOnly(date);
  const start = week.data.start_date ?? null;
  const end = week.data.end_date ?? null;
  if (start && end) return start <= dateStr && dateStr <= end;
  const bounds = boundsFromWeekRecord(week.data);
  return (
    formatDateOnly(bounds.start) <= dateStr &&
    dateStr <= formatDateOnly(bounds.end)
  );
}

combineLatest([childAuth$, records$]).subscribe(([s, [weekDocs, logDocs]]) => {
  const today = new Date();
  const userId = s.user!.id;

  const weeks = weekDocs.map(
    (d) => d.toJSON() as RecordDoc as unknown as WeekRecord,
  );
  const allLogs = logDocs.map(
    (d) => d.toJSON() as RecordDoc as unknown as JobLogRecord,
  );

  const myWeeks = weeks.filter((w) => w.user_id === userId);
  const myLogs = allLogs.filter((l) => l.user_id === userId);

  const currentWeek = myWeeks.find((w) => weekContainsDate(w, today)) ?? null;
  const previousWeek = (() => {
    // Anchor on the current week's start. Weeks are created on-demand (only
    // when a child logs a job), so when a new week begins there is usually no
    // current-week record yet — fall back to the start computed from today
    // under the family's offset. Without this fallback the lockout would never
    // trigger before the first log of the new week, letting the child skip
    // ahead and blocking confirmation of the still-open previous week.
    const currentStart = currentWeek
      ? parseDateOnly(
          currentWeek.data.start_date ??
            formatDateOnly(boundsFromWeekRecord(currentWeek.data).start),
        )
      : currentWeekBounds(familyService.weekStartDay(), today).start;
    return (
      myWeeks
        .filter((w) => {
          const end = parseDateOnly(
            w.data.end_date ?? formatDateOnly(boundsFromWeekRecord(w.data).end),
          );
          return end < currentStart;
        })
        .sort((a, b) =>
          (b.data.end_date ?? "").localeCompare(a.data.end_date ?? ""),
        )[0] ?? null
    );
  })();

  // Lockout: previous week is still `open` → child must confirm it before
  // they can move on to the current week.
  const isPreviousWeek =
    previousWeek !== null && previousWeek.data.status === "open";

  // Yellow pill: previous week is awaiting parent action.
  const pendingPreviousWeek =
    previousWeek !== null && previousWeek.data.status === "child_confirmed"
      ? previousWeek
      : null;

  const week = isPreviousWeek ? previousWeek : currentWeek;

  // Sibling competition mirrors the week the current child is viewing — match
  // each sibling's week record by iso_week + year so the leaderboard always
  // reflects a single calendar week.
  const siblingTotals: Record<string, number> = {};
  if (week) {
    for (const w of weeks) {
      if (w.user_id === userId || !w.user_id) continue;
      if (
        w.data.iso_week !== week.data.iso_week ||
        w.data.year !== week.data.year
      )
        continue;
      const total = allLogs
        .filter((l) => l.data.week_id === w.id)
        .reduce((sum, l) => sum + l.data.amount, 0);
      siblingTotals[w.user_id] = (siblingTotals[w.user_id] ?? 0) + total;
    }
  }

  if (!week) {
    state$.next({
      week: null,
      logs: [],
      siblingTotals,
      isPreviousWeek: false,
      pendingPreviousWeek,
      allWeeks: myWeeks,
      allLogs: myLogs,
      loading: false,
    });
    return;
  }

  const logs = allLogs
    .filter((l) => l.data.week_id === week.id)
    .sort((a, b) => a.created_at.localeCompare(b.created_at));
  state$.next({
    week,
    logs,
    siblingTotals,
    isPreviousWeek,
    pendingPreviousWeek,
    allWeeks: myWeeks,
    allLogs: myLogs,
    loading: false,
  });
});

function todayHHMM(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

async function patchRecordData(
  id: string,
  patch: Record<string, unknown>,
): Promise<void> {
  const db = await getDb();
  const doc = await db.week_allowance.findOne(id).exec();
  if (!doc) return;
  const json = doc.toJSON() as RecordDoc;
  const data = {
    ...(json.data as unknown as Record<string, unknown>),
    ...patch,
  };
  await doc.patch({
    data: data as unknown as Record<string, unknown>,
    updated_at: new Date().toISOString(),
  });
}

export const weekLogService = {
  state$: state$.asObservable(),

  get week(): WeekRecord | null {
    return state$.getValue().week;
  },
  get logs(): JobLogRecord[] {
    return state$.getValue().logs;
  },

  todayLogs(): JobLogRecord[] {
    const today = formatDateOnly(new Date());
    return state$.getValue().logs.filter((l) => loggedDateOf(l) === today);
  },

  weekTotal(): number {
    return state$.getValue().logs.reduce((sum, l) => sum + l.data.amount, 0);
  },

  todayTotal(): number {
    return weekLogService
      .todayLogs()
      .reduce((sum, l) => sum + l.data.amount, 0);
  },

  async logJob(
    job: JobRecord,
    note = "",
    logDate?: Date,
    amountOverride?: number,
  ): Promise<void> {
    const { user, profile } = getCurrentProfile();
    if (!user || !profile) return;
    // Prefer the active week from state (so we log into the previous week
    // when the child is in the lockout state). Fall back to create-on-demand
    // for the current week.
    const week =
      state$.getValue().week ??
      (await getOrCreateCurrentWeek(user.id, profile.data.family_id));
    if (week.data.status !== "open") throw new Error("Week is locked");

    if (profile.data.role === "child" && logDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const target = new Date(logDate);
      target.setHours(0, 0, 0, 0);
      if (target > today) throw new Error("Cannot log future days");
      const daysBack = Math.round(
        (today.getTime() - target.getTime()) / 86400000,
      );
      const maxBackdate = familyService.maxBackdateDays();
      if (daysBack > maxBackdate) {
        throw new Error("Day is outside the allowed backdate window");
      }
    }

    const amount = amountOverride ?? job.data.price ?? 0;

    const logData: JobLogData = {
      job_id: job.id,
      week_id: week.id,
      job_snapshot: {
        emoji: job.data.emoji,
        title: job.data.title,
        price: job.data.price,
        kind: job.data.kind,
      },
      amount,
      original_amount: amount,
      logged_time: todayHHMM(),
      logged_date: formatDateOnly(logDate ?? new Date()),
      status: job.data.requires_approval ? "pending" : "approved",
      note,
      parent_note: "",
      requires_approval: job.data.requires_approval,
      screen_time_hours: null,
      band_label: null,
    };

    const db = await getDb();
    const now = new Date().toISOString();
    await db.week_allowance.insert({
      id: crypto.randomUUID(),
      type: "job_log",
      family_id: profile.data.family_id,
      user_id: user.id,
      created_at: now,
      updated_at: now,
      data: logData as unknown as Record<string, unknown>,
    });
  },

  async logScreenTime(job: JobRecord, hours: number): Promise<void> {
    if (!job.data.bands) return;
    const band = resolveScreenBand(job.data.bands, hours);
    const { user, profile } = getCurrentProfile();
    if (!user || !profile) return;
    const week =
      state$.getValue().week ??
      (await getOrCreateCurrentWeek(user.id, profile.data.family_id));
    if (week.data.status !== "open") throw new Error("Week is locked");

    const logData: JobLogData = {
      job_id: job.id,
      week_id: week.id,
      job_snapshot: {
        emoji: job.data.emoji,
        title: job.data.title,
        price: job.data.price,
        kind: job.data.kind,
      },
      amount: band.amount,
      original_amount: band.amount,
      logged_time: todayHHMM(),
      logged_date: formatDateOnly(new Date()),
      status: "pending",
      note: "",
      parent_note: "",
      requires_approval: true,
      screen_time_hours: hours,
      band_label: band.label,
    };

    const db = await getDb();
    const now = new Date().toISOString();
    await db.week_allowance.insert({
      id: crypto.randomUUID(),
      type: "job_log",
      family_id: profile.data.family_id,
      user_id: user.id,
      created_at: now,
      updated_at: now,
      data: logData as unknown as Record<string, unknown>,
    });
  },

  async removeLog(logId: string): Promise<void> {
    const { week } = state$.getValue();
    if (week && week.data.status !== "open") throw new Error("Week is locked");
    const db = await getDb();
    const doc = await db.week_allowance.findOne(logId).exec();
    if (!doc) return;
    await doc.remove();
  },

  async confirmWeek(): Promise<void> {
    const { week } = state$.getValue();
    if (!week) return;
    if (week.data.status !== "open")
      throw new Error("Week is already confirmed");
    await patchRecordData(week.id, {
      status: "child_confirmed",
      child_confirmed_at: new Date().toISOString(),
    });
  },
};
